import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '@/App';
import { toast } from 'sonner';
import {
  Save, ExternalLink, X, Check, FileText,
  Bold, Italic, Underline, Strikethrough,
  Heading1, Heading2, Heading3, Pilcrow,
  List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight,
  Type, Highlighter, Undo2, Redo2,
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import UnderlineExt from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';

const TEXT_COLORS = [
  '#000000', '#dc2626', '#ea580c', '#ca8a04', '#16a34a',
  '#2563eb', '#7c3aed', '#db2777', '#64748b', '#ffffff',
];
const HIGHLIGHT_COLORS = [
  '#fef08a', '#bbf7d0', '#bfdbfe', '#fecaca', '#e9d5ff',
  '#fed7aa', '#fbcfe8', '#e2e8f0', '#ffffff',
];

/**
 * Right-side slide-in panel for editing/viewing node notes inline.
 * Opens when a node's file icon is clicked. Has an "Open in New Tab" button.
 */
const NodeNotesSidePanel = ({ open, mapId, nodeId, nodeLabel, mapName, onClose }) => {
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialContent, setInitialContent] = useState('');
  const [colorOpen, setColorOpen] = useState(false);
  const [highlightOpen, setHighlightOpen] = useState(false);
  const saveTimerRef = useRef(null);
  const colorRef = useRef(null);
  const highlightRef = useRef(null);
  const loadedKeyRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
      }),
      UnderlineExt,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-slate max-w-none focus:outline-none min-h-[55vh] px-5 py-4',
        'data-testid': 'side-note-editor',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => doSave(html), 1500);
    },
  });

  const doSave = useCallback(async (html) => {
    if (!mapId || !nodeId) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/mindmaps/${mapId}/nodes/${nodeId}/notes`,
        { content: html },
        { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
      );
      setLastSaved(new Date());
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }, [mapId, nodeId]);

  // Fetch notes when opened or node changes
  useEffect(() => {
    if (!open || !mapId || !nodeId || !editor) return;
    const key = `${mapId}::${nodeId}`;
    if (loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;
    setLoading(true);
    setLastSaved(null);
    (async () => {
      try {
        const res = await axios.get(
          `${API}/mindmaps/${mapId}/nodes/${nodeId}/notes`,
          { headers: getAuthHeader() }
        );
        const html = res.data.content || '';
        setInitialContent(html);
        if (!editor.isDestroyed) editor.commands.setContent(html);
      } catch {
        toast.error('Failed to load notes');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, mapId, nodeId, editor]);

  // Reset loaded key when panel closes so reopening refetches
  useEffect(() => {
    if (!open) {
      loadedKeyRef.current = null;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    }
  }, [open]);

  // Close color/highlight pickers on outside click
  useEffect(() => {
    const handler = (e) => {
      if (colorRef.current && !colorRef.current.contains(e.target)) setColorOpen(false);
      if (highlightRef.current && !highlightRef.current.contains(e.target)) setHighlightOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSave = useCallback(() => {
    if (!editor) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    doSave(editor.getHTML());
  }, [editor, doSave]);

  const openInNewTab = useCallback(() => {
    if (!mapId || !nodeId) return;
    // Ensure latest content is saved before opening new tab
    if (editor) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      doSave(editor.getHTML());
    }
    window.open(`/node-notes?mapId=${mapId}&nodeId=${nodeId}`, '_blank');
  }, [mapId, nodeId, editor, doSave]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop (click outside to close, transparent) */}
      <div
        className="fixed inset-0 z-30"
        onClick={onClose}
        data-testid="side-panel-backdrop"
      />

      {/* Side Panel */}
      <aside
        className="fixed top-0 right-0 h-screen w-[420px] max-w-[92vw] bg-white shadow-2xl border-l border-slate-200 z-40 flex flex-col animate-slide-in-right"
        data-testid="node-notes-side-panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate" data-testid="side-panel-node-label">
                {nodeLabel || 'Node Notes'}
              </div>
              {mapName && (
                <div className="text-[11px] text-slate-400 truncate">{mapName}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {lastSaved && (
              <span className="text-[11px] text-slate-400 flex items-center gap-0.5 mr-1">
                <Check className="w-3 h-3 text-green-500" />
                Saved
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="p-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
              title="Save"
              data-testid="side-panel-save-btn"
            >
              <Save className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={openInNewTab}
              className="p-1.5 rounded-lg border border-slate-300 hover:bg-slate-100 text-slate-700 transition-colors flex items-center gap-1"
              title="Open in New Tab"
              data-testid="side-panel-open-new-tab-btn"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500 transition-colors"
              title="Close"
              data-testid="side-panel-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <SideToolbar
          editor={editor}
          colorOpen={colorOpen}
          setColorOpen={setColorOpen}
          highlightOpen={highlightOpen}
          setHighlightOpen={setHighlightOpen}
          colorRef={colorRef}
          highlightRef={highlightRef}
        />

        {/* Editor body */}
        <div className="flex-1 overflow-y-auto bg-white side-editor-wrapper">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-slate-400 text-sm">
              Loading notes...
            </div>
          ) : (
            <EditorContent editor={editor} />
          )}
        </div>

        <style>{`
          @keyframes slide-in-right {
            from { transform: translateX(100%); opacity: 0.4; }
            to { transform: translateX(0); opacity: 1; }
          }
          .animate-slide-in-right { animation: slide-in-right 0.18s ease-out; }
          .side-editor-wrapper .tiptap {
            line-height: 1.7;
            font-size: 14px;
            color: #334155;
            font-family: 'Segoe UI', system-ui, sans-serif;
          }
          .side-editor-wrapper .tiptap:focus { outline: none; }
          .side-editor-wrapper .tiptap h1 {
            font-size: 22px; font-weight: 700; color: #1e293b; margin: 12px 0 6px; line-height: 1.3;
          }
          .side-editor-wrapper .tiptap h2 {
            font-size: 18px; font-weight: 600; color: #334155; margin: 10px 0 4px; line-height: 1.3;
          }
          .side-editor-wrapper .tiptap h3 {
            font-size: 16px; font-weight: 600; color: #475569; margin: 8px 0 4px; line-height: 1.4;
          }
          .side-editor-wrapper .tiptap p { margin: 4px 0; }
          .side-editor-wrapper .tiptap ul { list-style-type: disc; padding-left: 22px; margin: 6px 0; }
          .side-editor-wrapper .tiptap ol { list-style-type: decimal; padding-left: 22px; margin: 6px 0; }
          .side-editor-wrapper .tiptap li { margin: 2px 0; }
          .side-editor-wrapper .tiptap li p { margin: 0; }
          .side-editor-wrapper .tiptap blockquote {
            border-left: 3px solid #cbd5e1; padding-left: 14px; margin: 8px 0; color: #64748b;
          }
          .side-editor-wrapper .tiptap mark { padding: 1px 3px; border-radius: 2px; }
          .side-editor-wrapper .tiptap hr { border: none; border-top: 2px solid #e2e8f0; margin: 14px 0; }
          .side-editor-wrapper .tiptap p.is-editor-empty:first-child::before {
            content: 'Start writing your notes...';
            color: #94a3b8; float: left; pointer-events: none; height: 0;
          }
        `}</style>
      </aside>
    </>
  );
};

/* ── Compact Side Toolbar ── */
const SideToolbar = ({ editor, colorOpen, setColorOpen, highlightOpen, setHighlightOpen, colorRef, highlightRef }) => {
  if (!editor) return null;

  const TBtn = ({ onClick, active, title, children, testId }) => (
    <button
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`p-1 rounded transition-colors ${
        active
          ? 'bg-blue-100 text-blue-700'
          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
      }`}
    >
      {children}
    </button>
  );

  const Sep = () => <div className="w-px h-4 bg-slate-200 mx-0.5" />;

  return (
    <div className="flex items-center gap-0.5 px-2 py-1 border-b border-slate-100 flex-wrap bg-white" data-testid="side-editor-toolbar">
      <TBtn onClick={() => editor.chain().focus().undo().run()} title="Undo" testId="side-undo-btn">
        <Undo2 className="w-3.5 h-3.5" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().redo().run()} title="Redo" testId="side-redo-btn">
        <Redo2 className="w-3.5 h-3.5" />
      </TBtn>
      <Sep />
      <TBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold" testId="side-bold-btn">
        <Bold className="w-3.5 h-3.5" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic" testId="side-italic-btn">
        <Italic className="w-3.5 h-3.5" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline" testId="side-underline-btn">
        <Underline className="w-3.5 h-3.5" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough" testId="side-strike-btn">
        <Strikethrough className="w-3.5 h-3.5" />
      </TBtn>
      <Sep />
      <TBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="H1" testId="side-h1-btn">
        <Heading1 className="w-3.5 h-3.5" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="H2" testId="side-h2-btn">
        <Heading2 className="w-3.5 h-3.5" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="H3" testId="side-h3-btn">
        <Heading3 className="w-3.5 h-3.5" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive('paragraph') && !editor.isActive('heading')} title="Paragraph" testId="side-paragraph-btn">
        <Pilcrow className="w-3.5 h-3.5" />
      </TBtn>
      <Sep />
      <TBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List" testId="side-bullet-list-btn">
        <List className="w-3.5 h-3.5" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered List" testId="side-ordered-list-btn">
        <ListOrdered className="w-3.5 h-3.5" />
      </TBtn>
      <Sep />
      <TBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align Left" testId="side-align-left-btn">
        <AlignLeft className="w-3.5 h-3.5" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align Center" testId="side-align-center-btn">
        <AlignCenter className="w-3.5 h-3.5" />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align Right" testId="side-align-right-btn">
        <AlignRight className="w-3.5 h-3.5" />
      </TBtn>
      <Sep />

      {/* Text color */}
      <div className="relative" ref={colorRef}>
        <TBtn
          onClick={() => { setColorOpen(!colorOpen); setHighlightOpen(false); }}
          title="Text Color"
          testId="side-text-color-picker"
        >
          <Type className="w-3.5 h-3.5" />
        </TBtn>
        {colorOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 p-2 z-50 grid grid-cols-5 gap-1.5 min-w-[140px]">
            {TEXT_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => { editor.chain().focus().setColor(c).run(); setColorOpen(false); }}
                className="w-6 h-6 rounded-full border border-slate-200 hover:scale-125 transition-transform"
                style={{ backgroundColor: c }}
              />
            ))}
            <button
              onClick={() => { editor.chain().focus().unsetColor().run(); setColorOpen(false); }}
              className="col-span-5 text-xs text-slate-500 hover:text-slate-700 mt-1"
            >
              Reset
            </button>
          </div>
        )}
      </div>

      {/* Highlight */}
      <div className="relative" ref={highlightRef}>
        <TBtn
          onClick={() => { setHighlightOpen(!highlightOpen); setColorOpen(false); }}
          title="Highlight"
          testId="side-highlight-picker"
        >
          <Highlighter className="w-3.5 h-3.5" />
        </TBtn>
        {highlightOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 p-2 z-50 grid grid-cols-5 gap-1.5 min-w-[140px]">
            {HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => { editor.chain().focus().toggleHighlight({ color: c }).run(); setHighlightOpen(false); }}
                className="w-6 h-6 rounded-full border border-slate-200 hover:scale-125 transition-transform"
                style={{ backgroundColor: c }}
              />
            ))}
            <button
              onClick={() => { editor.chain().focus().unsetHighlight().run(); setHighlightOpen(false); }}
              className="col-span-5 text-xs text-slate-500 hover:text-slate-700 mt-1"
            >
              Remove
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NodeNotesSidePanel;
